import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchProviders, phoneStart, phoneVerify } from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { apiErrorCode, authErrorMessage } from '../auth/errors';

const STORAGE_KEY = 'vbbplay.phone_flow';

interface PhoneFlow {
  phone: string;
  masked: string;
  resendAt: number;
  purpose: 'login' | 'link';
}

function loadFlow(purpose: 'login' | 'link'): PhoneFlow | null {
  try {
    const flow = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null') as PhoneFlow | null;
    return flow && flow.purpose === purpose ? flow : null;
  } catch {
    return null;
  }
}

export function PhoneAuthPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const { user, loading, applyUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const purpose: 'login' | 'link' = params.get('mode') === 'link' ? 'link' : 'login';
  const [flow, setFlow] = useState<PhoneFlow | null>(() => loadFlow(purpose));
  const [phone, setPhone] = useState(flow?.phone ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [needsReauth, setNeedsReauth] = useState(false);
  const [phoneAuthOn, setPhoneAuthOn] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchProviders()
      .then((status) => {
        if (!cancelled) setPhoneAuthOn(Boolean(status.sms.enabled));
      })
      .catch(() => {
        if (!cancelled) setPhoneAuthOn(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!flow) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [flow]);

  useEffect(() => {
    if (flow) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(flow));
    else sessionStorage.removeItem(STORAGE_KEY);
  }, [flow]);

  if (loading || phoneAuthOn === null) return <div className="page"><div className="skeleton" style={{ minHeight: 220 }} /></div>;
  if (purpose === 'login' && user) return <Navigate to="/" replace />;
  if (purpose === 'link' && !user) return <Navigate to="/auth/login" replace />;
  if (!phoneAuthOn) {
    return (
      <div className="page auth-page auth-sheet-page">
        <article className="auth-card auth-sheet">
          <h1>{purpose === 'link' ? t('auth:phoneLinkTitle') : t('auth:phoneTitle')}</h1>
          <p className="auth-error" role="alert">{t('auth:errors.phone_auth_disabled')}</p>
          <p>
            <Link to={purpose === 'link' ? '/security' : '/auth/login'}>
              {purpose === 'link' ? t('auth:securityTitle') : t('auth:backToLogin')}
            </Link>
          </p>
        </article>
      </div>
    );
  }

  function handle(err: unknown) {
    if (apiErrorCode(err) === 'reauth_required') setNeedsReauth(true);
    setError(authErrorMessage(t, err));
  }

  async function send(target: string) {
    setError(null);
    setSubmitting(true);
    try {
      const res = await phoneStart(target, purpose);
      setFlow({ phone: target, masked: res.phone_masked, resendAt: Date.now() + res.resend_in * 1000, purpose });
      setCode('');
    } catch (err) {
      handle(err);
    } finally {
      setSubmitting(false);
    }
  }

  async function onSend(event: FormEvent) {
    event.preventDefault();
    await send(phone.trim());
  }

  async function onVerify(event: FormEvent) {
    event.preventDefault();
    if (!flow) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await phoneVerify(flow.phone, code.trim(), purpose);
      setFlow(null);
      if (res.session) {
        applyUser(res.session.user);
        navigate('/');
      } else if (res.user) {
        applyUser(res.user);
        navigate('/security', { state: { notice: 'phoneLinked' } });
      }
    } catch (err) {
      handle(err);
    } finally {
      setSubmitting(false);
    }
  }

  const waitSeconds = flow ? Math.max(0, Math.ceil((flow.resendAt - now) / 1000)) : 0;

  return (
    <div className="page auth-page auth-sheet-page">
      <article className="auth-card auth-sheet">
        <h1>{purpose === 'link' ? t('auth:phoneLinkTitle') : t('auth:phoneTitle')}</h1>
        {!flow ? (
          <form className="auth-form" onSubmit={onSend}>
            <label className="field">
              <span>{t('auth:phoneLabel')}</span>
              <input
                className="field-input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+994 50 123 45 67"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={32}
                required
              />
            </label>
            <p className="muted-note">{t('auth:phoneHint')}</p>
            {error ? <p className="auth-error" role="alert">{error}</p> : null}
            <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
              {submitting ? t('common:loading') : t('auth:sendCode')}
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={onVerify}>
            <p>{t('auth:codeSentTo', { phone: flow.masked })}</p>
            <label className="field">
              <span>{t('auth:codeLabel')}</span>
              <input
                className="field-input otp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </label>
            {error ? <p className="auth-error" role="alert">{error}</p> : null}
            <button type="submit" className="btn btn-primary btn-block" disabled={submitting || code.length !== 6}>
              {submitting ? t('common:loading') : t('auth:verifyCode')}
            </button>
            <div className="auth-row">
              <button
                type="button"
                className="btn btn-ghost btn-compact"
                disabled={submitting || waitSeconds > 0}
                onClick={() => void send(flow.phone)}
              >
                {waitSeconds > 0 ? t('auth:resendIn', { seconds: waitSeconds }) : t('auth:resendCode')}
              </button>
              <button type="button" className="btn btn-ghost btn-compact" onClick={() => setFlow(null)}>
                {t('auth:changeNumber')}
              </button>
            </div>
          </form>
        )}
        {needsReauth ? (
          <p className="muted-note">
            <Link to="/security">{t('auth:reauthTitle')}</Link>
          </p>
        ) : null}
        <p>
          <Link to={purpose === 'link' ? '/security' : '/auth/login'}>
            {purpose === 'link' ? t('auth:securityTitle') : t('auth:backToLogin')}
          </Link>
        </p>
      </article>
    </div>
  );
}
